import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { PageHeader } from '../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../components/PageHeader/Button';
import { notificationActions } from '../../components/Notification/redux/actions';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import Strings from '../../Strings';
import {
  actions,
  APPROVE_ACCESS_BILLING_DISPOSITION_RESULT,
  ASSIGN_ACCESS_BILLING_CLINICIANS_RESULT,
  CONFIRM_ACCESS_CARE_DELIVERY_RESULT,
  CREATE_ACCESS_BILLING_DISPOSITION_RESULT,
  GET_ACCESS_BILLING_CLINICIANS_RESULT,
  GET_ACCESS_BILLING_DISPOSITION_EVENTS_RESULT,
  GET_ACCESS_GCODE_GENERATIONS_RESULT,
  TRANSITION_ACCESS_BILLING_DISPOSITION_RESULT,
} from './actions';

import './AccessGCodes.scss';

const responseMessage = response => (
  response?.response?.data?.standard_error?.message
  || response?.response?.data?.error?.message
  || response?.response?.data?.message
  || Strings.accessGCodesActionFailed
);

const statusLabel = status => status?.replaceAll('_', ' ') || '-';
const clinicianLabel = clinician => `${clinician.display_name} (${clinician.npi})`;
const qualificationCodes = (qualification, key) => (
  Array.isArray(qualification?.[key]) && qualification[key].length > 0
    ? qualification[key].join(', ') : '-'
);

function AccessGCodes({
  getGenerations,
  confirmCareDelivery,
  createDisposition,
  getBillingClinicians,
  assignBillingClinicians,
  approveDisposition,
  transitionDisposition,
  getDispositionEvents,
  showNotification,
}) {
  const [generations, setGenerations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [events, setEvents] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [careDelivery, setCareDelivery] = useState({ service_started_on: '', place_of_service: '10', place_of_service_reason: '' });
  const [assignment, setAssignment] = useState({ rendering_roster_member_id: '', supervising_roster_member_id: '' });
  const [transition, setTransition] = useState({ action: '', reason: '' });

  const loadGenerations = useCallback(() => {
    setIsLoading(true);
    return getGenerations().then(response => {
      if (response?.type === GET_ACCESS_GCODE_GENERATIONS_RESULT) {
        const next = response.response?.generations || [];
        setGenerations(next);
        setSelected(current => current ? next.find(item => item.id === current.id) || null : null);
      } else {
        showNotification(responseMessage(response), 7000, true);
      }
    }).finally(() => setIsLoading(false));
  }, [getGenerations, showNotification]);

  const loadDispositionDetails = useCallback(generation => {
    const disposition = generation?.billing_disposition;
    if (!disposition) {
      setClinicians([]);
      setEvents([]);
      return;
    }
    getDispositionEvents(disposition.id).then(response => {
      if (response?.type === GET_ACCESS_BILLING_DISPOSITION_EVENTS_RESULT) {
        setEvents(response.response?.events || []);
      } else {
        showNotification(responseMessage(response), 7000, true);
      }
    });
    if (disposition.status === 'draft') {
      getBillingClinicians(disposition.id).then(response => {
        if (response?.type === GET_ACCESS_BILLING_CLINICIANS_RESULT) {
          setClinicians(response.response || []);
        } else {
          showNotification(responseMessage(response), 7000, true);
        }
      });
    } else {
      setClinicians([]);
    }
  }, [getBillingClinicians, getDispositionEvents, showNotification]);

  useEffect(() => { loadGenerations(); }, [loadGenerations]);
  useEffect(() => {
    loadDispositionDetails(selected);
    const disposition = selected?.billing_disposition;
    setAssignment({
      rendering_roster_member_id: disposition?.rendering_roster_member_id?.toString() || '',
      supervising_roster_member_id: disposition?.supervising_roster_member_id?.toString() || '',
    });
    setTransition({ action: '', reason: '' });
  }, [selected, loadDispositionDetails]);

  const refresh = () => loadGenerations();
  const run = (request, successType, successMessage) => {
    setIsSaving(true);
    return request.then(response => {
      if (response?.type !== successType) {
        showNotification(responseMessage(response), 7000, true);
        return false;
      }
      showNotification(successMessage, 4000);
      return refresh().then(() => true);
    }).finally(() => setIsSaving(false));
  };

  const onConfirmCareDelivery = () => {
    if (!careDelivery.service_started_on || !careDelivery.place_of_service
      || (careDelivery.place_of_service !== '10' && !careDelivery.place_of_service_reason.trim())) {
      showNotification(Strings.accessGCodesCompleteRequiredFields, 5000, true);
      return;
    }
    run(
      confirmCareDelivery(selected.id, careDelivery),
      CONFIRM_ACCESS_CARE_DELIVERY_RESULT,
      Strings.accessGCodesCareDeliveryConfirmed,
    );
  };

  const onCreateDisposition = () => run(
    createDisposition(selected.id),
    CREATE_ACCESS_BILLING_DISPOSITION_RESULT,
    Strings.accessGCodesDispositionCreated,
  );

  const onAssignClinicians = () => {
    if (!assignment.rendering_roster_member_id) {
      showNotification(Strings.accessGCodesRenderingRequired, 5000, true);
      return;
    }
    const data = {
      rendering_roster_member_id: Number(assignment.rendering_roster_member_id),
      supervising_roster_member_id: assignment.supervising_roster_member_id
        ? Number(assignment.supervising_roster_member_id) : undefined,
    };
    run(
      assignBillingClinicians(selected.billing_disposition.id, data),
      ASSIGN_ACCESS_BILLING_CLINICIANS_RESULT,
      Strings.accessGCodesCliniciansAssigned,
    );
  };

  const onApprove = () => run(
    approveDisposition(selected.billing_disposition.id),
    APPROVE_ACCESS_BILLING_DISPOSITION_RESULT,
    Strings.accessGCodesDispositionApproved,
  );

  const transitionDefinitions = {
    'needs-review': { label: Strings.accessGCodesSendToReview, reasonRequired: true },
    'resolve-review': { label: Strings.accessGCodesResolveReview, reasonRequired: false },
    cancel: { label: Strings.accessGCodesCancelDisposition, reasonRequired: true },
  };

  const onTransition = () => {
    const definition = transitionDefinitions[transition.action];
    if (!definition) return;
    if (definition.reasonRequired && !transition.reason.trim()) {
      showNotification(Strings.accessGCodesReasonRequired, 5000, true);
      return;
    }
    run(
      transitionDisposition(selected.billing_disposition.id, transition.action, {
        reason: transition.reason,
      }),
      TRANSITION_ACCESS_BILLING_DISPOSITION_RESULT,
      Strings.accessGCodesDispositionUpdated,
    );
  };

  const columns = useMemo(() => [
    columnHelper.accessor('patient_name', { header: Strings.patientNameText, cell: info => info.getValue() || '-', size: 210 }),
    columnHelper.accessor('track', { header: Strings.accessTrack, accessorFn: row => row.diagnosis_qualification?.track || '-', size: 90 }),
    columnHelper.accessor('g_code', { header: Strings.accessGCodesCode, cell: info => info.getValue() || '-', size: 90 }),
    columnHelper.accessor('period_start_date', { header: Strings.accessGCodesPeriod, accessorFn: row => `${row.period_start_date} – ${row.period_end_date}`, size: 210 }),
    columnHelper.accessor('status', { header: Strings.status, cell: info => statusLabel(info.getValue()), size: 190 }),
    columnHelper.accessor('dos', { header: Strings.accessGCodesDos, cell: info => info.getValue() || '-', size: 110 }),
    columnHelper.accessor('submission_deadline_on', { header: Strings.accessGCodesDeadline, cell: info => info.getValue() || '-', size: 130 }),
    columnHelper.accessor('billing_disposition', { header: Strings.accessGCodesDisposition, cell: info => statusLabel(info.getValue()?.status), size: 180 }),
  ], []);

  const disposition = selected?.billing_disposition;
  const canTransition = (disposition && {
    ready_for_submission: ['needs-review', 'cancel'],
    needs_review: ['resolve-review', 'cancel'],
    submitted_externally: ['cancel'],
    draft: ['cancel'],
  }[disposition.status]) || [];

  return (
    <div className="access-gcodes">
      <PageHeader
        left={Strings.accessGCodesTitle}
        right={<Button class={HEADER_BUTTON_DARK_BLUE} title={Strings.refresh} onClick={loadGenerations} />}
      />
      <Table3
        name="access-gcode-generations"
        data={generations}
        columns={columns}
        isLoading={isLoading}
        onRowClick={row => setSelected(row)}
        noPagination
        enableColumnFiltering
        pinFirstColumn
      />
      {selected && (
        <section className="access-gcodes__details" data-testid="access-gcode-details">
          <header>
            <div>
              <h2>{`${selected.g_code || Strings.accessGCodesCandidate} · ${selected.period_start_date}`}</h2>
              <p>{`${Strings.accessGCodesStatus}: ${statusLabel(selected.status)}`}</p>
            </div>
            <button type="button" className="close-button" aria-label={Strings.close} onClick={() => setSelected(null)} />
          </header>
          <div className="access-gcodes__metadata">
            <span><strong>{Strings.accessGCodesDiagnosis}:</strong> {selected.icd10cm || '-'}</span>
            <span><strong>{Strings.accessGCodesPolicy}:</strong> {selected.diagnosis_policy_version || '-'}</span>
            <span><strong>{Strings.accessGCodesPos}:</strong> {selected.place_of_service || '-'}</span>
            <span><strong>{Strings.accessGCodesDeadline}:</strong> {selected.submission_deadline_on || '-'}</span>
          </div>
          {selected.diagnosis_qualification && (
            <section className="access-gcodes__qualification">
              <h3>{Strings.accessGCodesQualification}</h3>
              <span><strong>{Strings.accessGCodesQualificationDecision}:</strong> {statusLabel(selected.diagnosis_qualification.decision)}</span>
              <span><strong>{Strings.accessGCodesQualifyingCodes}:</strong> {qualificationCodes(selected.diagnosis_qualification, 'qualifying_codes')}</span>
              <span><strong>{Strings.accessGCodesExclusionCodes}:</strong> {qualificationCodes(selected.diagnosis_qualification, 'exclusion_codes')}</span>
              <span><strong>{Strings.accessGCodesEnrollmentCodes}:</strong> {qualificationCodes(selected.diagnosis_qualification, 'patient_condition_codes')}</span>
            </section>
          )}

          {selected.status === 'pending_care_delivery' && (
            <div className="access-gcodes__form">
              <h3>{Strings.accessGCodesConfirmCareDelivery}</h3>
              <label htmlFor="access-gcodes-service-started-on">{Strings.accessGCodesServiceStartedOn}</label>
              <input id="access-gcodes-service-started-on" type="date" value={careDelivery.service_started_on} onChange={event => setCareDelivery(current => ({ ...current, service_started_on: event.target.value }))} />
              <label htmlFor="access-gcodes-place-of-service">{Strings.accessGCodesPos}</label>
              <input id="access-gcodes-place-of-service" value={careDelivery.place_of_service} maxLength="2" onChange={event => setCareDelivery(current => ({ ...current, place_of_service: event.target.value }))} />
              {careDelivery.place_of_service !== '10' && <><label htmlFor="access-gcodes-place-of-service-reason">{Strings.accessGCodesPosReason}</label><textarea id="access-gcodes-place-of-service-reason" value={careDelivery.place_of_service_reason} onChange={event => setCareDelivery(current => ({ ...current, place_of_service_reason: event.target.value }))} /></>}
              <button type="button" className="brand-blue" disabled={isSaving} onClick={onConfirmCareDelivery}>{Strings.accessGCodesConfirmCareDelivery}</button>
            </div>
          )}

          {selected.status === 'ready_for_claim' && !disposition && (
            <button type="button" className="brand-blue" disabled={isSaving} onClick={onCreateDisposition}>{Strings.accessGCodesCreateDisposition}</button>
          )}

          {disposition?.status === 'draft' && (
            <div className="access-gcodes__form">
              <h3>{Strings.accessGCodesAssignClinicians}</h3>
              <label htmlFor="access-gcodes-rendering-clinician">{Strings.accessGCodesRenderingClinician}</label>
                <select id="access-gcodes-rendering-clinician" value={assignment.rendering_roster_member_id} onChange={event => setAssignment(current => ({ ...current, rendering_roster_member_id: event.target.value }))}>
                  <option value="">{Strings.select}</option>
                  {clinicians.map(clinician => <option value={clinician.id} key={clinician.id}>{clinicianLabel(clinician)}</option>)}
                </select>
              <label htmlFor="access-gcodes-supervising-clinician">{Strings.accessGCodesSupervisingClinician}</label>
                <select id="access-gcodes-supervising-clinician" value={assignment.supervising_roster_member_id} onChange={event => setAssignment(current => ({ ...current, supervising_roster_member_id: event.target.value }))}>
                  <option value="">{Strings.accessGCodesNoSupervisingClinician}</option>
                  {clinicians.filter(clinician => clinician.id.toString() !== assignment.rendering_roster_member_id).map(clinician => <option value={clinician.id} key={clinician.id}>{clinicianLabel(clinician)}</option>)}
                </select>
              <button type="button" className="brand-white-gray" disabled={isSaving} onClick={onAssignClinicians}>{Strings.accessGCodesAssignClinicians}</button>
              <button type="button" className="brand-blue" disabled={isSaving || !assignment.rendering_roster_member_id} onClick={onApprove}>{Strings.accessGCodesApproveDisposition}</button>
            </div>
          )}

          {canTransition.length > 0 && (
            <div className="access-gcodes__form">
              <h3>{Strings.accessGCodesDispositionActions}</h3>
              <label htmlFor="access-gcodes-disposition-action">{Strings.accessGCodesAction}</label>
                <select id="access-gcodes-disposition-action" value={transition.action} onChange={event => setTransition(current => ({ ...current, action: event.target.value }))}>
                  <option value="">{Strings.select}</option>
                  {canTransition.map(action => <option key={action} value={action}>{transitionDefinitions[action].label}</option>)}
                </select>
              {transition.action && transitionDefinitions[transition.action].reasonRequired && <><label htmlFor="access-gcodes-transition-reason">{Strings.accessGCodesReason}</label><textarea id="access-gcodes-transition-reason" value={transition.reason} onChange={event => setTransition(current => ({ ...current, reason: event.target.value }))} /></>}
              <button type="button" className="brand-blue" disabled={isSaving || !transition.action} onClick={onTransition}>{Strings.accessGCodesUpdateDisposition}</button>
            </div>
          )}

          {disposition?.billing_snapshot && (
            <details className="access-gcodes__snapshot">
              <summary>{Strings.accessGCodesAcceptedSnapshot}</summary>
              <pre>{JSON.stringify(disposition.billing_snapshot, null, 2)}</pre>
            </details>
          )}
          {disposition && (
            <div className="access-gcodes__events">
              <h3>{Strings.accessGCodesAuditHistory}</h3>
              {events.length === 0 ? <p>{Strings.accessGCodesNoEvents}</p> : events.map(event => (
                <div key={event.id}>{`${event.created_at}: ${statusLabel(event.from_status)} → ${statusLabel(event.to_status)}${event.reason ? ` — ${event.reason}` : ''}`}</div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

AccessGCodes.propTypes = {
  getGenerations: PropTypes.func.isRequired,
  confirmCareDelivery: PropTypes.func.isRequired,
  createDisposition: PropTypes.func.isRequired,
  getBillingClinicians: PropTypes.func.isRequired,
  assignBillingClinicians: PropTypes.func.isRequired,
  approveDisposition: PropTypes.func.isRequired,
  transitionDisposition: PropTypes.func.isRequired,
  getDispositionEvents: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  getGenerations: () => dispatch(actions.getGenerations()),
  confirmCareDelivery: (generationId, data) => dispatch(actions.confirmCareDelivery(generationId, data)),
  createDisposition: generationId => dispatch(actions.createDisposition(generationId)),
  getBillingClinicians: dispositionId => dispatch(actions.getBillingClinicians(dispositionId)),
  assignBillingClinicians: (dispositionId, data) => dispatch(actions.assignBillingClinicians(dispositionId, data)),
  approveDisposition: dispositionId => dispatch(actions.approveDisposition(dispositionId)),
  transitionDisposition: (dispositionId, transition, data) => dispatch(actions.transitionDisposition(dispositionId, transition, data)),
  getDispositionEvents: dispositionId => dispatch(actions.getDispositionEvents(dispositionId)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
});

export default connect(null, mapDispatchToProps)(AccessGCodes);
